/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Snackbar } from '@mui/material';
import InstaLoader from 'components/LoaderComponent/InstaLoader';
import { AppBridge } from 'infrastructure/appBridge/appBridge';
import { APP_BRIDGE_CALLBACKS } from 'infrastructure/appBridge/appCallBacks';
import analyticsService from 'infrastructure/serviceHandlers/Analytics/Analytics';
import { ScreenName } from 'infrastructure/serviceHandlers/Analytics/eventMapper';
import { useScreenView } from 'infrastructure/serviceHandlers/Analytics/useScreenView';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreateSession } from 'services/api/UserApi';
import { useAppDispatch } from 'store/hooks';
import { fetchSpaceDevices } from 'store/reducers/deviceInSpaceReducers';
import { fetchOwnerSpace } from 'store/reducers/spaceReducers';
import { fetchAllSubscription } from 'store/reducers/subscriptionReducer';
import { fetchUser } from 'store/reducers/userReducers';
import { clearReduxStates } from 'store/store';
import { bootFail, bootMark, bootTag } from 'utils/bootTrace';
import { setLocalStorageItem } from 'utils/commonFunctions';
import { QUERY_PARAMS } from 'utils/constants';
import {
  setProductTypeStorage,
  setSessionStorageItem
} from 'utils/sessionFunctions';
import {
  armSessionGate,
  rejectSessionGate,
  resolveSessionGate
} from 'utils/sessionGate';
import { fetchWebViewAccessToken } from 'utils/webviewHelpers';

function AppBridgePage() {
  const [isLoading] = useState(true);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useScreenView(ScreenName.LOADING_SCREEN);

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const sessionId = query.get(QUERY_PARAMS.SESSION_ID) || '';
  setSessionStorageItem(QUERY_PARAMS.SESSION_ID, sessionId);


  // for amplitude user path tracking
  const amplitudeUserPath =
    query.get(QUERY_PARAMS.USER_PATH) || query.get(QUERY_PARAMS.URL_PATH) || '';

  const handleSnackBarCloseButton = useCallback(() => {
    setShowSnackBar(false);
  }, []);

  const handleCloseWebview = useCallback(() => {
    AppBridge.sendMessageToApp(APP_BRIDGE_CALLBACKS.CLOSE_IN_APP_WEBVIEW, true);
  }, []);

  const getRedirectionURL = useCallback(() => {
    const redirectPath = query.get(QUERY_PARAMS.REDIRECT_PATH) || '';

    if (!redirectPath) {
      // Close webview if no redirect path is present
      setErrorMessage('No redirect path provided');
      setShowSnackBar(true);
      handleCloseWebview();
      return '/';
    }

    query.delete(QUERY_PARAMS.REDIRECT_PATH);

    const finalRedirectionUrl = `/${redirectPath}${
      query.toString() ? `?${query.toString()}` : ''
    }`;

    return finalRedirectionUrl;
  }, [query]);

  // Runs in the background AFTER navigate(): the destination page renders its
  // skeleton while these round trips happen, instead of after them. Every data
  // call the destination fires is queued on the session gate until the cookie
  // exists, so nothing races CreateSession.
  const establishSession = useCallback(async () => {
    try {
      // Cached token first — the app refreshes it in the background, and the
      // unconditional forced refresh was 0.5–2s of network on every open. An
      // empty cached token (or a stale one, below) falls back to a real refresh.
      bootMark('token_start');
      bootTag('token_mode', 'cached');
      let webviewToken = await fetchWebViewAccessToken(false);
      if (!webviewToken) {
        // Empty cache — this open pays the full refresh, same as the baseline.
        bootTag('token_mode', 'cached_empty_forced');
        webviewToken = await fetchWebViewAccessToken(true);
      }
      bootMark('token_end');

      bootMark('session_start');
      try {
        await CreateSession({ id_token: webviewToken });
      } catch (err) {
        // Stale cached token → exactly one retry with a forced refresh.
        // skipSessionGate on CreateSession keeps the axios interceptor from
        // closing the webview on this 401 before the retry runs.
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 401 || status === 403) {
          // Stale-cache retry: the leg cost both a forced refresh and a second
          // CreateSession, so tag it — these opens are the slow tail.
          bootTag('token_mode', 'cached_stale_retried');
          webviewToken = await fetchWebViewAccessToken(true);
          await CreateSession({ id_token: webviewToken });
        } else {
          throw err;
        }
      }
      bootMark('session_end');

      resolveSessionGate();
    } catch (err) {
      console.error('Error establishing webview session', err);
      bootFail('token_or_session');
      // Every request queued on the gate rejects with this error.
      rejectSessionGate(err);
      // A webview with no session is unusable — hand control back to the app,
      // mirroring what the axios interceptor does for a mid-session 401.
      handleCloseWebview();
    }
  }, [dispatch, handleCloseWebview]);

  useEffect(() => {
    // Preserve the user's language across the wipe: i18nextLng drives the
    // Accept-Language header and which translation chunk loads. Clearing it
    // made every open re-detect the language and re-download translations
    // late in the boot (English flash for non-English users).
    const userLanguage = localStorage.getItem('i18nextLng');
    localStorage.clear();
    if (userLanguage) {
      localStorage.setItem('i18nextLng', userLanguage);
    }
    clearReduxStates();

    // Which product the app was showing when it opened this webview — read
    // by RouteProductTheme as a theming fallback for routes whose own URL has
    // no product segment (payment/billing/camera/data routes). Must be
    // written AFTER the wipe above, or it is cleared before anything reads it.
    setProductTypeStorage(
      QUERY_PARAMS.PRODUCT_TYPE_QUERY,
      query.get(QUERY_PARAMS.PRODUCT_TYPE_QUERY) || ''
    );
    const postPairingFlow = query.get(QUERY_PARAMS.POST_PAIRING_FLOW) || '';
    setLocalStorageItem(QUERY_PARAMS.POST_PAIRING_FLOW, postPairingFlow);

    // device_id is only present in the pairing flow, and is used to resolve the post-pairing flow for the paired device. It is not present in the app-injected flow, and is not used in any other flows.
    const deviceId = query.get(QUERY_PARAMS.DEVICE_ID) || '';

    // Read the redirect before establishSession/navigate: getRedirectionURL
    // deletes the param from `query`.
    const redirectionUrl = getRedirectionURL();
    bootMark('bridge_mount');
    armSessionGate();

    // Prewarm shared state before the session exists — these queue on the gate
    // and fire the moment CreateSession lands. Dispatching them here (rather
    // than after establishSession) flips their slices to isLoading immediately,
    // so destination pages show a skeleton instead of briefly reading
    // "not loading, no data" as an error. The destination's own fetches
    // collapse into these via { dedupe: true }.
    dispatch(fetchUser());
    dispatch(fetchOwnerSpace())
      .unwrap()
      .then((response) =>
        dispatch(fetchSpaceDevices(response?.id))
          .unwrap()
          .then((devices) => {
            // Read the paired device's post-pairing flow once, here, and persist
            // it under the same key the app's injected value uses. Downstream,
            // the popups that close the webview just read a plain string — no
            // lookup in component scope, and it survives the localStorage wipe
            // that runs on every webview open.
            //
            // Skipped when the app injected a flow itself (older builds): that
            // value wins, so those builds behave exactly as they do today.
            if (!deviceId || postPairingFlow) return;
            const device = devices?.find((item) => item.id === deviceId);
            // Absent when the model has no post-pairing flow configured.
            setLocalStorageItem(
              QUERY_PARAMS.POST_PAIRING_FLOW,
              device?.pairing_modes?.post_pairing || ''
            );
          })
      )
      .catch((err) =>
        // Leave the flow empty — closing the webview then falls through to
        // pro-monitoring or a plain close, same as an unconfigured model.
        console.log('error while resolving post pairing flow', err)
      );
    dispatch(fetchAllSubscription({ dedupe: true }));

    establishSession();
    // Navigate immediately — don't wait for the token or CreateSession. The
    // destination chunk is already prefetched (see src/index.tsx) and its data
    // calls queue on the session gate.
    navigate(redirectionUrl, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (amplitudeUserPath) {
      analyticsService.setUserPathInLocalStorage(amplitudeUserPath);
    } else {
      localStorage.removeItem('user_path');
    }
  }, [amplitudeUserPath]);

  return (
    <>
      {isLoading && <InstaLoader />}
      <Snackbar
        open={showSnackBar}
        autoHideDuration={2500}
        onClose={handleSnackBarCloseButton}
      >
        <Alert
          onClose={handleSnackBarCloseButton}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {`Error: ${errorMessage} `}
        </Alert>
      </Snackbar>
    </>
  );
}
export default memo(AppBridgePage);
